import React, { useState, useEffect } from 'react';
import { Stack, Table, Title, Select, MultiSelect, Paper, TextInput, Group, Button, Textarea, Text, Tabs } from '@mantine/core';
import {  IconSearch, IconFilter, IconX, IconExternalLink, IconUsers, IconChartBar, IconHeartRateMonitor , IconTool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import SystemResourceChart from './SystemResourceChart';
import AiLogsViewer from './AiLogsViewer';
import AdminVpsMonitorTab from './AdminVpsMonitorTab';
import AdminSkillsTab from './AdminSkillsTab';
import AdminRentalTab from './AdminRentalTab';


export default function AdminPanel({ user, apiBaseUrl }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  
  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [filterRole, setFilterRole] = useState([]);
  const [filterStatus, setFilterStatus] = useState('ALL');

  const fetchUsers = () => {
    if (!user) return;
    fetch(`${apiBaseUrl}/api/users?adminEmail=${user.email}`)
      .then(res => res.json())
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(console.error);
  };

  useEffect(() => {
    fetchUsers();
    if (user?.email === 'office@ailinkb.com' || user?.role?.includes('ADMIN')) {
      fetch(`${apiBaseUrl}/api/config?ownerEmail=office@ailinkb.com`)
        .then(res => res.json())
        .then(data => {
          if (!data['ai.global.rules']) {
            data['ai.global.rules'] = "CRITICAL RULE: You are a professional AI Assistant. Behave naturally, conversationally, and amicably like a friendly human being. Reply EXACTLY in the language of the User's message. DO NOT add translation notes or meta-commentary. Always respond in a CONCISE, brief, and to-the-point manner (around 2-4 clear sentences) unless the user explicitly asks you to elaborate or expand. If the user simply greets you or says a casual hello (like 'hey', 'hello', 'what's up', 'cześć', 'hejka'), respond warmly, naturally, and gently like a human, asking whether they would like to search for information/services OR set up and register their own Website with an automated AI Chatbot today (e.g., 'Cześć! W czym mogę Ci dzisiaj pomóc? Chcesz wyszukać informacje o usługach, czy chciałbyś założyć własną stronę internetową z wbudowanym Chatbotem AI?'). NEVER proactively start lecturing them with details from the database context or company profiles unless the user actually asked about companies, products, or services. If the user expresses interest in setting up a website with a chatbot, politely guide them to collect ALL required registration fields: 1) Company / Project Name, 2) Contact Email Address, and 3) A short description of what the company does and its services. Once all three required fields are provided, inform them that their website is being generated and an email with a preview link and subscription activation link has just been sent, and output the registration action tag. IMPORTANT CONVERSATION CONTINUITY: When concluding an interaction, when answering a question about a service, or when the user says thank you / acknowledges an answer, ALWAYS ask if there is anything ELSE or any OTHER topic you can help with (e.g., 'Is there anything else I can help you with today?' / 'Czy jest jeszcze coś innego, w czym mógłbym Ci pomóc?'). NEVER repeat the initial opening phrase 'Is there anything I can help you with?' once a topic has already been discussed.";
          }
          setAdminConfig(data);
        })
        .catch(console.error);
    }
  }, [user]);

  const [adminConfig, setAdminConfig] = useState({});
  const [savingPrompts, setSavingPrompts] = useState(false);

  const saveAdminPrompts = () => {
    setSavingPrompts(true);
    fetch(`${apiBaseUrl}/api/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...adminConfig, ownerEmail: 'office@ailinkb.com' })
    })
      .then(res => res.json())
      .then(() => {
        setSavingPrompts(false);
        alert(t('admin_prompts_saved'));
      })
      .catch(err => {
        console.error(err);
        setSavingPrompts(false);
      });
  };

  const handleRoleChange = (userId, newRolesArray) => {
    fetch(`${apiBaseUrl}/api/users/${userId}?adminEmail=${user.email}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRolesArray.join(',') })
    }).then(fetchUsers);
  };

  const handleStatusChange = (userId, newStatus) => {
    fetch(`${apiBaseUrl}/api/users/${userId}?adminEmail=${user.email}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionStatus: newStatus })
    }).then(fetchUsers);
  };



  const handleCompanyNameChange = (userId, newName) => {
    fetch(`${apiBaseUrl}/api/users/${userId}?adminEmail=${user.email}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName: newName })
    }).then(fetchUsers);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm("Are you sure you want to delete this user completely? This action cannot be undone.")) {
      fetch(`${apiBaseUrl}/api/users/${userId}?adminEmail=${user.email}`, {
        method: 'DELETE'
      }).then(fetchUsers);
    }
  };

  const clearFilters = () => {
    setSearchEmail('');
    setFilterRole([]);
    setFilterStatus('ALL');
  };

  const filteredUsers = users.filter(u => {
    // Hide casual users (those who just used chat and didn't register profile or buy anything)
    const isAdmin = u.role?.includes('ADMIN');
    const boughtAccess = (u.subscriptionPlan && u.subscriptionPlan !== 'FREE') || 
                         (u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'PAST_DUE' || u.subscriptionStatus === 'CANCELED');
    const registeredProfile = u.companyName && u.companyName.trim().length > 0;
    
    // Show users who have UNVERIFIED status regardless of their profile completion
    if (!isAdmin && !boughtAccess && !registeredProfile && u.subscriptionStatus !== 'UNVERIFIED') {
      return false;
    }

    const matchesEmail = searchEmail === '' || u.email?.toLowerCase().includes(searchEmail.toLowerCase());
    const matchesRole = filterRole.length === 0 || filterRole.some(r => u.role?.includes(r));
    const matchesStatus = filterStatus === 'ALL' || u.subscriptionStatus === filterStatus;
    return matchesEmail && matchesRole && matchesStatus;
  });

  return (
    <Tabs defaultValue="users" keepMounted={false} radius="md" variant="pills" color="indigo" p="md">
      <Tabs.List mb="md">
        <Tabs.Tab value="users" leftSection={<IconUsers size={16}/>}>Użytkownicy i System</Tabs.Tab>
        <Tabs.Tab value="skills">Weryfikacja Skilli</Tabs.Tab>
        <Tabs.Tab value="vps" leftSection={<IconHeartRateMonitor size={16}/>}>{t('vpsMonitor.tabName')}</Tabs.Tab>
        <Tabs.Tab value="rentals" leftSection={<IconTool size={16}/>}>{t('rental_admin_tab', 'Wypożyczalnia')}</Tabs.Tab>
      </Tabs.List>
      
      <Tabs.Panel value="users">
        <Stack gap="xl">
          <Title order={2}>{t('admin_title')}</Title>
      
      <Paper p="md" withBorder shadow="sm" bg="#141517">
        <Group align="flex-end" mb="md">
          <TextInput
            label={t('admin_search_email')}
            placeholder={t('admin_search_email_placeholder')}
            leftSection={<IconSearch size={16} />}
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            style={{ flex: 1 }}
          />
          <MultiSelect
            label={t('admin_filter_role')}
            placeholder={t('admin_all_roles')}
            data={['USER', 'ADMIN', 'PRZYPOMINKA']}
            value={filterRole}
            onChange={setFilterRole}
            style={{ width: '220px' }}
          />
          <Select
            label={t('admin_filter_status')}
            data={['ALL', 'TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'UNVERIFIED']}
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: '180px' }}
          />

          <Button 
            variant="filled" color="red" 
            leftSection={<IconX size={16} />}
            onClick={clearFilters}
          >
            {t('admin_clear')}
          </Button>
        </Group>
      </Paper>

      <Paper p="md" withBorder shadow="sm">
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('admin_id')}</Table.Th>
              <Table.Th>{t('admin_email')}</Table.Th>
              <Table.Th>{t('admin_company')}</Table.Th>
              <Table.Th>{t('admin_role')}</Table.Th>
              <Table.Th>{t('admin_subscription')}</Table.Th>

              <Table.Th>{t('admin_actions', 'Akcje')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredUsers.map(u => (
              <Table.Tr key={u.id}>
                <Table.Td>{u.id}</Table.Td>
                <Table.Td fw={500}>{u.email}</Table.Td>
                <Table.Td>
                  <TextInput 
                    size="xs"
                    placeholder="Company Name"
                    defaultValue={u.companyName || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (u.companyName || '')) {
                        handleCompanyNameChange(u.id, e.target.value);
                      }
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  <MultiSelect 
                    size="xs"
                    data={['USER', 'ADMIN', 'PRZYPOMINKA']}
                    value={u.role ? u.role.split(',') : ['USER']}
                    onChange={(v) => handleRoleChange(u.id, v)}
                  />
                </Table.Td>
                <Table.Td>
                  <Select 
                    size="xs"
                    data={['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'UNVERIFIED']}
                    value={u.subscriptionStatus || 'TRIAL'}
                    onChange={(v) => handleStatusChange(u.id, v)}
                  />
                </Table.Td>

                <Table.Td>
                  <Group gap="xs">
                    <Button 
                      size="xs" 
                      variant="light" 
                      color="indigo" 
                      leftSection={<IconExternalLink size={14} />} 
                      onClick={() => window.open(`/?profile=${btoa(u.email)}`, '_blank')}
                    >
                      {t('admin_view_profile', 'Zobacz Profil')}
                    </Button>
                    <Button 
                      size="xs" 
                      variant="filled" color="red" 
                      onClick={() => handleDeleteUser(u.id)}
                    >
                      <IconX size={14} />
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {filteredUsers.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  {t('admin_no_users')}
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {(user?.role?.includes('ADMIN') || user?.email === 'office@ailinkb.com') && (
        <Paper p="xl" withBorder shadow="sm" radius="md">
          <Stack gap="md">
            <Title order={3} c="indigo">{t('admin_prompts_title')}</Title>
            <Text size="sm" c="dimmed">{t('admin_prompts_desc')}</Text>
            
            <Textarea
              label={t('admin_global_rules_label')}
              description={t('admin_global_rules_desc')}
              minRows={5}
              autosize
              maxRows={12}
              value={adminConfig['ai.global.rules'] || ''}
              onChange={(e) => setAdminConfig({ ...adminConfig, 'ai.global.rules': e.target.value })}
              styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
            />

            <Textarea
              label={t('admin_default_system_prompt_label')}
              description={t('admin_default_system_prompt_desc')}
              minRows={5}
              autosize
              maxRows={12}
              value={adminConfig['ai.system.prompt'] || ''}
              onChange={(e) => setAdminConfig({ ...adminConfig, 'ai.system.prompt': e.target.value })}
              styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
            />

            <Textarea
              label={t('admin_autoreply_prompt_label')}
              description={t('admin_autoreply_prompt_desc')}
              minRows={4}
              autosize
              maxRows={10}
              value={adminConfig['ai.autoreply.prompt'] || ''}
              onChange={(e) => setAdminConfig({ ...adminConfig, 'ai.autoreply.prompt': e.target.value })}
              styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
            />

            <Textarea
              label={t('admin_action_protocol_label', 'Action Protocol (Tags & Automations)')}
              description={t('admin_action_protocol_desc', 'Instructions for AI to output action tags like ###REGISTER_WEBSITE:{...}### or ###BOOK_MEETING:{...}###. These tags trigger automated actions in the backend.')}
              minRows={4}
              autosize
              maxRows={10}
              value={adminConfig['ai.action.protocol'] || ''}
              onChange={(e) => setAdminConfig({ ...adminConfig, 'ai.action.protocol': e.target.value })}
              styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
            />
            <Group justify="flex-end">
              <Button color="indigo" loading={savingPrompts} onClick={saveAdminPrompts}>
                {t('admin_save_prompts')}
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      <SystemResourceChart user={user} apiBaseUrl={apiBaseUrl} />
        <AiLogsViewer user={user} apiBaseUrl={apiBaseUrl} />
        </Stack>
      </Tabs.Panel>
      
        <Tabs.Panel value="skills">
          <AdminSkillsTab adminEmail={user?.email} />
        </Tabs.Panel>
        
        <Tabs.Panel value="vps">
          <AdminVpsMonitorTab user={user} apiBaseUrl={apiBaseUrl} />
        </Tabs.Panel>
        
        <Tabs.Panel value="rentals">
          <AdminRentalTab apiBaseUrl={apiBaseUrl} />
        </Tabs.Panel>
    </Tabs>
  );
}
